import {IAnyObject} from "../../common/SimpleInterface";
import {isArray, isNativeObject, isNumeric, phpToMomentDateFormat} from "../../common/SiteHelper";
import {TextFieldTypes} from "@ionic/core";
import {FormItemType} from "../../../tsx/component/form/model/FormModel";
import SimpleChoice, {ISimpleChoices} from "../../common/SimpleChoiceVO";
import {PostType} from "../post/PostManager";

export type FieldType =
    "group" |
    "repeater" |
    "text" |
    "textarea" |
    "wysiwyg" |
    "checkbox" |
    "radio" |
    "true_false" |
    "date_time_picker" |
    "file" |
    "map_location" |
    "hidden" |
    "image" |
    "select" |
    "tab"

const fieldTypes:FieldType[] = [
    "group",
    "repeater",
    "text",
    "textarea",
    "wysiwyg",
    "checkbox",
    "radio",
    "true_false",
    "date_time_picker",
    "file",
    "map_location",
    "hidden",
    "image",
    "select",
    "tab"
];

class PostFieldConditionalLogicItem
{
    key:string = "";
    operator:string = "";
    value:any = null;
    valid:boolean = false;

    constructor(data:any)
    {
        if(!isNativeObject(data))
        {
            return;
        }
        this.key = data.field ?? "";
        this.operator = ["==", "!="].includes(data.operator) ? data.operator : "";
        this.value = data.value;
        this.valid = !!this.key && !!this.operator && (this.value !== undefined);
    }
}
class PostFieldConditionalGroup
{
    items:PostFieldConditionalLogicItem[] = [];
    valid:boolean = false;

    constructor(data:any)
    {
        if(!isArray(data))
        {
            return;
        }
        for(let item of data){
            const conditionalLogicItem = new PostFieldConditionalLogicItem(item);
            if(conditionalLogicItem.valid)
            {
                this.items.push(conditionalLogicItem);
            }
        }
        this.valid = !!this.items.length;
    }
}

export class PostFieldConfigItem
{
    fieldType:FieldType;
    name:string;
    key:string;

    path:(string|string[])[];
    inGroup:boolean = false;
    inRepeater:boolean = false;

    label:string;
    className:string;
    classNames:string[];
    required:boolean;

    instructions?:string;
    placeholder?:string;
    defaultValue?:any;
    value?:any;

    minLength?:number;
    maxLength?:number;
    minValue?:number;
    maxValue?:number;
    rowMin?:number;
    rowMax?:number;

    mimeTypes?:string;
    fileCountMin:number;
    fileLimit?:number;
    fileSizeMin?:number;
    fileSizeMax?:number;
    imageWidthMin?:number;
    imageWidthMax?:number;
    imageHeightMin?:number;
    imageHeightMax?:number;

    dateDisplayFormat?:string;
    parentOverrideFieldName?:string;

    textFieldType?:TextFieldTypes;
    formItemType?:FormItemType;

    choices:ISimpleChoices = {};
    choiceList:SimpleChoice[];
    subfields:IPostFieldConfigItems = {}
    subfieldsByKey:IPostFieldConfigItems = {}
    subFieldList:PostFieldConfigItem[];

    conditionalLogic:PostFieldConditionalGroup[] = [];

    data:IAnyObject;
    valid:boolean;
    hiddenFromSubmit:boolean = false;

    constructor(data:IAnyObject, path:(string|string[])[] = [])
    {
        const fromClassName= data.class ?? "";
        const classNames = fromClassName.split(" ");
        for(let i = 0; i < classNames.length; i ++){
            const className = classNames[i].trim();
            if(!className){
                classNames.splice(i, 1);
                i --;
                continue;
            }
            classNames[i] = className;
        }
        let fromFieldType = data.field_type;
        let textFieldType:TextFieldTypes|null = null;
        if(fromFieldType === "email"){
            textFieldType = "email";
        } else if(fromFieldType === "url"){
            textFieldType = "url";
        } else if(fromFieldType === "text"){
            if(classNames.includes("phone")){
                textFieldType = "tel";
            }
        } else if(fromFieldType === "google_map"){
            fromFieldType = "map_location";
        }
        let formItemType:FormItemType|null = null;
        if(fromFieldType === "true_false"){
            formItemType = "boolean";
        } else if(classNames.includes("integer")){
            formItemType = "int";
        } else if(classNames.includes("float")){
            formItemType = "float";
        }

        this.className = fromClassName;
        this.classNames = classNames;

        this.textFieldType = textFieldType ?? undefined;
        this.formItemType = formItemType ?? undefined;

        const fieldType:FieldType = !textFieldType ? fromFieldType : "text";
        let valid = fieldTypes.includes(fieldType);
        if(classNames.includes("convert_to_hidden")){
            this.hiddenFromSubmit = true;
        }

        let fromCategoryList = data.categories ?? [];
        let fromChoiceList = data.choices ?? [];
        let fromSubFieldList = data.sub_fields ?? [];

        fromCategoryList = Array.isArray(fromCategoryList) ? fromCategoryList : [];
        fromChoiceList = Array.isArray(fromChoiceList) ? fromChoiceList : [];
        fromSubFieldList = Array.isArray(fromSubFieldList) ? fromSubFieldList : [];


        const choiceList:SimpleChoice[] = [];
        for(let fromChoice of fromChoiceList){
            if(!isNativeObject(fromChoice)){
                continue;
            }
            const choice = new SimpleChoice(fromChoice.id ?? "", fromChoice.name ?? "");
            if(!choice.id || !!this.choices[choice.id]){
                continue;
            }
            this.choices[choice.id] = choice;
            choiceList.push(choice);
        }
        if(!choiceList.length){
            for(let fromCategory of fromCategoryList){
                if(!isNativeObject(fromCategory)){
                    continue;
                }
                const choice = new SimpleChoice(parseInt(fromCategory.term_id ?? 0), fromCategory.name ?? "");
                choice.slug = fromCategory.slug ?? null;
                if(!choice.id || !!this.choices[choice.id]){
                    continue;
                }
                this.choices[choice.id] = choice;
                choiceList.push(choice);
            }
        }
        if(fieldType === "checkbox" || fieldType === "radio" || fieldType === "select"){
            if(!choiceList.length){
                valid = false;
            }
        }

        const name:string = data.name ?? ""

        const subFieldList:PostFieldConfigItem[] = [];

        for(let fromSubField of fromSubFieldList){
            if(!isNativeObject(fromSubField)){
                continue;
            }
            let subFieldPath = path.slice();
            if(fieldType === "repeater"){
                subFieldPath.push([name])
            } else {
                subFieldPath.push(name)
            }
            const subField = new PostFieldConfigItem(fromSubField, subFieldPath);
            if(!subField.valid || !!this.subfields[subField.name]){
                continue;
            }
            this.subfields[subField.name] = subField;
            subFieldList.push(subField);
            const thisKeySubFields:PostFieldConfigItem[] = [];
            if(subField.key){
                thisKeySubFields.push(subField);
            }
            for(let key in subField.subfieldsByKey){
                thisKeySubFields.push(subField.subfieldsByKey[key]);
            }
            for(let checkSubfield of thisKeySubFields)
            {
                if(!this.subfieldsByKey[checkSubfield.key]){
                    this.subfieldsByKey[checkSubfield.key] = checkSubfield;
                } else {
                    this.subfieldsByKey[checkSubfield.key] = preferredField(this.subfieldsByKey[checkSubfield.key], checkSubfield);
                }
            }
        }
        if(fieldType === "group" || fieldType === "repeater"){
            if(!subFieldList.length){
                valid = false;
            }
        }

        this.fieldType = fieldType;

        this.name = name;
        this.key = data.key ?? "";

        this.name = this.name ? this.name: this.key;
        this.key = this.key ? this.key: this.name;
        this.path = path;
        for(let i = 0; i < path.length; i++){
            if(isArray(path[i])){
                this.inRepeater = true;
            } else {
                this.inGroup = true;
            }
        }

        this.label = data.label ?? "";
        this.required = !!(data.required ?? false);

        const instructions = data.instructions ?? "";
        const placeholder = data.placeholder ?? "";
        const defaultValue = data.default ?? null;
        const value = data.value ?? null;

        const minLength = parseInt(data.minlength ?? 0);
        const maxLength = parseInt(data.maxlength ?? 0);
        const minValue = data.minvalue ?? null;
        const maxValue = data.maxvalue ?? null;
        const rowMax = parseInt(data.row_limit ?? 0);
        const rowMin = parseInt(data.row_min ?? 0);

        this.instructions = instructions ? instructions: undefined;
        this.placeholder = placeholder ? placeholder : undefined;
        this.defaultValue = defaultValue ? defaultValue : undefined;
        this.value = value ? value : undefined;

        this.minLength = minLength ? minLength : undefined;
        this.maxLength = maxLength ? maxLength : undefined;
        if(this.fieldType === "date_time_picker"){
            this.minValue = isNumeric(minValue) ? parseInt(minValue) : (minValue ?? undefined);
            this.maxValue = isNumeric(maxValue) ? parseInt(maxValue) : (maxValue ?? undefined);
        } else if(this.formItemType === "int"){
            this.minValue = isNumeric(minValue) ? parseInt(minValue) : undefined;
            this.maxValue = isNumeric(maxValue) ? parseInt(maxValue) : undefined;
        } else if(this.formItemType === "float"){
            this.minValue = isNumeric(minValue) ? parseFloat(minValue) : undefined;
            this.maxValue = isNumeric(maxValue) ? parseFloat(maxValue) : undefined;
        }
        this.rowMax = rowMax ? rowMax : undefined;
        this.rowMin = rowMin ? rowMin : undefined;

        const dateDisplayFormat = data.date_display_format ?? undefined;
        this.dateDisplayFormat = dateDisplayFormat ? phpToMomentDateFormat(dateDisplayFormat) : undefined;

        const mimeTypes = data.mime_types ?? null;
        const fileCountMin = parseInt(data.file_count_min ?? 0);
        const fileLimit = parseInt(data.file_limit ?? 0);
        const fileSizeMin = parseInt(data.file_size_min ?? 0);
        const fileSizeMax = parseInt(data.file_size_max ?? 0);
        const imageWidthMin = parseInt(data.image_width_min ?? 0);
        const imageWidthMax = parseInt(data.image_width_max ?? 0);
        const imageHeightMin = parseInt(data.image_height_min ?? 0);
        const imageHeightMax = parseInt(data.image_height_max ?? 0);

        this.mimeTypes = mimeTypes ? mimeTypes: undefined;
        this.fileCountMin = fileCountMin >= 0 ? fileCountMin : 0;
        this.fileLimit = fileLimit > 0 ? Math.max(fileLimit, this.fileCountMin) : undefined;
        this.fileSizeMin = fileSizeMin >= 0 ? fileSizeMin : 0;
        this.fileSizeMax = fileSizeMax > 0 ? Math.max(fileSizeMax, this.fileSizeMin) : undefined;
        this.imageWidthMin = imageWidthMin >= 0 ? imageWidthMin : 0;
        this.imageWidthMax = imageWidthMax > 0 ? Math.max(imageWidthMax, this.imageWidthMin) : undefined;
        this.imageHeightMin = imageHeightMin >= 0 ? imageHeightMin : 0;
        this.imageHeightMax = imageHeightMax > 0 ? Math.max(imageHeightMax, this.imageHeightMin) : undefined;
        this.parentOverrideFieldName = data.parent_override_field_name ?? undefined


        this.choiceList = choiceList;
        this.subFieldList = subFieldList;

        const fromConditionalLogic = isArray(data.conditional_logic) ? data.conditional_logic : [];
        for(let fromConditionalLogicGroup of fromConditionalLogic){
            const conditionalLogicGroup = new PostFieldConditionalGroup(fromConditionalLogicGroup);
            if(conditionalLogicGroup.valid){
                this.conditionalLogic.push(conditionalLogicGroup);
            }
        }


        this.data = {};
        for(let key in data){
            const value = data[key];
            if(value){
                this.data[key] = value;
            }
        }
        this.valid = valid;

    }
}

export type IPostFieldConfigItems = {
    [key in string]: PostFieldConfigItem
};

export class PostFieldSubmitTab
{
    label:string = "";
    itemList:PostFieldConfigItem[] = [];
    conditionalLogic:PostFieldConditionalGroup[] = [];
}


export default class PostFieldConfig
{
    postType:PostType;

    submitTabs:PostFieldSubmitTab[] = [];


    items:IPostFieldConfigItems = {};
    itemList:PostFieldConfigItem[] = [];
    itemsByKey:IPostFieldConfigItems = {};


    constructor(postType:PostType)
    {
        this.postType = postType;
    }

    apply(fromList:any[]){
        this.submitTabs.length = 0;
        let submitTab:PostFieldSubmitTab|null = null;
        for(let fromItem of fromList){

            if(!isNativeObject(fromItem)){
                continue;
            }
            const item = new PostFieldConfigItem(fromItem);
            if(!item.valid){
                continue;
            }
            if(item.fieldType === "tab" || !submitTab){
                submitTab = new PostFieldSubmitTab();

                const fromConditionalLogic = isArray(fromItem.conditional_logic) ? fromItem.conditional_logic : [];
                for(let fromConditionalLogicGroup of fromConditionalLogic){
                    const conditionalLogicGroup = new PostFieldConditionalGroup(fromConditionalLogicGroup);
                    if(conditionalLogicGroup.valid){
                        submitTab.conditionalLogic.push(conditionalLogicGroup);
                    }
                }
                this.submitTabs.push(submitTab);
                if(item.fieldType === "tab"){
                    submitTab.label = item.label;
                    item.hiddenFromSubmit = true;
                    continue;
                }
            }
            this.items[item.name] = item;
            this.itemList.push(item);

            if(!item.hiddenFromSubmit){
                submitTab.itemList.push(item);
            }
            const thisKeyItems:PostFieldConfigItem[] = [];
            if(item.key){
                thisKeyItems.push(item);
            }
            for(let key in item.subfieldsByKey){
                thisKeyItems.push(item.subfieldsByKey[key]);
            }
            for(let checkItem of thisKeyItems)
            {
                if(!this.itemsByKey[checkItem.key]){
                    this.itemsByKey[checkItem.key] = checkItem;
                } else {
                    this.itemsByKey[checkItem.key] = preferredField(this.itemsByKey[checkItem.key], checkItem);
                }
            }
        }
    }
}

const preferredField = (fieldA:PostFieldConfigItem, fieldB:PostFieldConfigItem):PostFieldConfigItem => {
    if(!fieldA.inRepeater && fieldB.inRepeater){
        return fieldA;
    }
    if(fieldA.inRepeater && !fieldB.inRepeater){
        return fieldB;
    }
    if(!fieldA.inGroup && fieldB.inGroup){
        return fieldA;
    }
    if(fieldA.inGroup && !fieldB.inGroup){
        return fieldB;
    }
    if(fieldA.path.length <= fieldB.path.length){
        return fieldA;
    }
    return fieldB;
}